import { lazy, Suspense, useMemo, type CSSProperties } from 'react';
import type { EChartsOption } from 'echarts';
import { useTranslation } from 'react-i18next';
import { u340xBuildDualAxisOption } from '../utils/u340xBuildDualAxisOption';
import type { U340xKpiMetricConfig, U340xKpiSeriesPoint } from '../types/u340xKpiTypes';
import { NS } from '../u340xKpiNs';

const ReactEChartsLazy = lazy(() => import('./U340xKpiEchartsCore'));

export interface U340xKpiDualAxisChartProps {
  series: U340xKpiSeriesPoint[];
  primaryMetric: U340xKpiMetricConfig & { axis: 'left' };
  secondaryMetric: U340xKpiMetricConfig & { axis: 'right' };
  primaryStackMetric?: U340xKpiMetricConfig & { axis: 'left' };
  secondaryExtraMetric?: U340xKpiMetricConfig & { axis: 'right' };
  secondaryTecMetric?: U340xKpiMetricConfig & { axis: 'right' };
  secondaryTecExtraMetric?: U340xKpiMetricConfig & { axis: 'right' };
  onMonthClick?: (month: string) => void;
  selectedMonth?: string | null;
  style?: CSSProperties;
  className?: string;
}

function U340xKpiDualAxisChart({
  series,
  primaryMetric,
  secondaryMetric,
  primaryStackMetric,
  secondaryExtraMetric,
  secondaryTecMetric,
  secondaryTecExtraMetric,
  onMonthClick,
  selectedMonth,
  style,
  className,
}: U340xKpiDualAxisChartProps) {
  const { t } = useTranslation(NS);

  const option: EChartsOption = useMemo(
    () =>
      u340xBuildDualAxisOption({
        series,
        primaryMetric,
        secondaryMetric,
        primaryStackMetric,
        secondaryExtraMetric,
        secondaryTecMetric,
        secondaryTecExtraMetric,
        selectedMonth,
      }),
    [
      series,
      primaryMetric,
      secondaryMetric,
      primaryStackMetric,
      secondaryExtraMetric,
      secondaryTecMetric,
      secondaryTecExtraMetric,
      selectedMonth,
    ],
  );

  const onEvents = useMemo(() => {
    if (!onMonthClick) {
      return undefined;
    }
    return {
      click: (params: unknown) => {
        const p = params as { name?: string; dataIndex?: number };
        const fromName = typeof p.name === 'string' ? p.name : undefined;
        if (fromName) {
          onMonthClick(fromName);
          return;
        }
        if (typeof p.dataIndex === 'number' && series[p.dataIndex]) {
          onMonthClick(series[p.dataIndex].month);
        }
      },
    };
  }, [onMonthClick, series]);

  return (
    <Suspense
      fallback={
        <div className="flex h-64 items-center justify-center text-sm text-muted-foreground">
          {t('chart.loading')}
        </div>
      }
    >
      <ReactEChartsLazy
        option={option}
        style={{ height: '18rem', width: '100%', ...style }}
        className={className}
        notMerge
        lazyUpdate={false}
        onEvents={onEvents}
      />
    </Suspense>
  );
}

export default U340xKpiDualAxisChart;
